import React from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  Collapse,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import BookmarkAddRoundedIcon from '@mui/icons-material/BookmarkAddRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import { ColumnFlexBox, CenteredFlexBox } from '../../FlexBox';
import useTranslate from '../../../Hooks/useTranslate';
import SectionLabel from '../../SectionLabel';
import { EntryListRow } from './LayoutRows';
import SortableRows, { reordered } from './SortableRows';
import SettingsFields from './SettingsFields';
import { alongPath } from './useLayoutEdit';
import { settingsFor } from '../settings';
import {
  WIDGET_TYPES,
  widgetUsesEntries,
  widgetUsesRegions,
} from '../detail/WidgetRegistry';

/** What a region may set for the keys added to it: an entry's own settings, less its wording. */
const DEFAULTABLE = settingsFor({}, 'detail').filter(
  ({ name }) => name !== 'label'
);

/**
 * What the region chosen on the canvas is and what it shows.
 *
 * Its fields are listed by name alone and opened one at a time; what a field holds - the cell that
 * draws it, how wide it sits, the vocabulary behind it - is a page of its own, since a column the
 * width of a sidebar shows three of those at once and a dozen names.
 *
 * Where the region sits and how big it is are not here either: those are the canvas's, and dragging
 * says them better than a number does.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.region - The region being configured.
 * @param {Function} props.onChange - Receives the changes to the region.
 * @param {Function} props.onEntries - Receives a mapper over the region's entries.
 * @param {Function} props.onOpenEntry - Receives the index of the field to open.
 * @param {Function} props.onAddEntry - Asks for the keys the region could still show.
 * @param {Function} props.onOpenRegions - Opens the children of a container region.
 * @param {Function} props.onSavePreset - Saves the region as a preset the palette will offer.
 *
 * @returns {React.JSX.Element}
 */
const RegionConfig = function RegionConfig({
  region,
  onChange = () => {},
  onEntries = () => {},
  onOpenEntry = () => {},
  onAddEntry = () => {},
  onOpenRegions = () => {},
  onSavePreset = () => {},
}) {
  const intl = useIntl();
  const translate = useTranslate();
  const [showDefaults, setShowDefaults] = React.useState(false);

  // A region that names no widget is drawn by `fields`, so it is configured as one.
  const widget = region.widget ?? 'fields';
  const entries = region.entries ?? [];

  return (
    <ColumnFlexBox gap="12px" sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
      <Select
        size="small"
        value={widget}
        onChange={(e) => onChange({ widget: e.target.value })}
        sx={{ flex: 'none' }}
      >
        {WIDGET_TYPES.map(({ widget, label }) => (
          <MenuItem key={widget} value={widget}>
            {translate(label)}
          </MenuItem>
        ))}
      </Select>
      <TextField
        size="small"
        label={intl.messages.LABEL}
        value={region.label ?? ''}
        onChange={(e) => onChange({ label: e.target.value })}
        sx={{ flex: 'none' }}
      />

      {/* A region reading a collection of the record draws one of it at a time, and its keys are
          then that one's rather than the record's. */}
      {widgetUsesEntries(widget) && (
        <TextField
          size="small"
          label={intl.messages.SOURCE}
          value={region.source ?? ''}
          onChange={(e) => onChange({ source: e.target.value || undefined })}
          sx={{ flex: 'none' }}
        />
      )}

      {/* How much room the region leaves: around what it holds, and between one entry and the next. */}
      {widgetUsesEntries(widget) && (
        <CenteredFlexBox gap="8px" sx={{ flex: 'none' }}>
          <TextField
            size="small"
            label={intl.messages.PADDING}
            value={region.padding ?? ''}
            placeholder="16px"
            onChange={(e) => onChange({ padding: e.target.value || undefined })}
            sx={{ flex: 1, minWidth: 0 }}
          />
          <TextField
            size="small"
            type="number"
            label={intl.messages.SPACING}
            value={region.spacing ?? ''}
            onChange={(e) =>
              onChange({
                spacing: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            sx={{ width: '90px', flex: 'none' }}
            slotProps={{ htmlInput: { min: 0, max: 8, step: 0.5 } }}
          />
        </CenteredFlexBox>
      )}

      {/* What a key added here starts as, for a region whose entries are alike. */}
      {widgetUsesEntries(widget) && (
        <ColumnFlexBox gap="8px" sx={{ flex: 'none' }}>
          <Button
            id="entry-defaults-btn"
            size="small"
            startIcon={<TuneRoundedIcon />}
            onClick={() => setShowDefaults((held) => !held)}
            sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
          >
            {`${intl.messages.ENTRY_DEFAULTS} (${
              Object.values(region.entryDefaults ?? {}).filter(
                (held) => held !== undefined
              ).length
            })`}
          </Button>
          <Collapse in={showDefaults} unmountOnExit>
            <SettingsFields
              settings={DEFAULTABLE}
              values={region.entryDefaults ?? {}}
              onChange={(changes) =>
                onChange({
                  entryDefaults: {
                    ...(region.entryDefaults ?? {}),
                    ...changes,
                  },
                })
              }
            />
          </Collapse>
        </ColumnFlexBox>
      )}

      {/* A container holds regions rather than keys, and they are listed rather than placed. */}
      {widgetUsesRegions(widget) && (
        <Button
          id="open-regions-btn"
          startIcon={<ViewListRoundedIcon />}
          onClick={() => onOpenRegions(region.id)}
          sx={{ flex: 'none', justifyContent: 'flex-start' }}
        >
          {`${intl.messages.REGIONS} (${(region.regions ?? []).length})`}
        </Button>
      )}

      {widgetUsesEntries(widget) && (
        <ColumnFlexBox gap="8px" sx={{ flex: 1, minHeight: 0 }}>
          <SectionLabel>{intl.messages.COLUMNS}</SectionLabel>
          {entries.length === 0 && (
            <Typography variant="body2" sx={{ opacity: '70%' }}>
              {intl.messages.PICK_KEYS_FOR_REGION}
            </Typography>
          )}
          <SortableRows
            items={entries}
            onReorder={(from, to) =>
              onEntries((held) => reordered(held, from, to))
            }
          >
            {(entry, index) => (
              <ColumnFlexBox gap="4px">
                <EntryListRow
                  entry={entry}
                  index={index}
                  onOpen={onOpenEntry}
                  onRemove={(position) =>
                    onEntries((held) => held.filter((_, at) => at !== position))
                  }
                />
                {/* A group holds entries of its own, listed under it: they are configured like any
                    other, and dragged within the group rather than out of it. */}
                {entry.kind === 'group' && (
                  <ColumnFlexBox gap="4px" sx={{ pl: '20px' }}>
                    <SortableRows
                      items={entry.entries ?? []}
                      onReorder={(from, to) =>
                        onEntries((held) =>
                          alongPath(held, [index], (list) =>
                            reordered(list, from, to)
                          )
                        )
                      }
                    >
                      {(child, position) => (
                        <EntryListRow
                          entry={child}
                          index={position}
                          onOpen={() => onOpenEntry([index, position])}
                          onRemove={(at) =>
                            onEntries((held) =>
                              alongPath(held, [index], (list) =>
                                list.filter((_, place) => place !== at)
                              )
                            )
                          }
                        />
                      )}
                    </SortableRows>
                    <Button
                      id={`add-into-${entry.id ?? entry.key}`}
                      size="small"
                      startIcon={<AddRoundedIcon />}
                      onClick={() => onAddEntry(index)}
                      sx={{ flex: 'none', justifyContent: 'flex-start' }}
                    >
                      {intl.messages.ADD}
                    </Button>
                  </ColumnFlexBox>
                )}
              </ColumnFlexBox>
            )}
          </SortableRows>
          {/* Called with nothing, not handed the handler: `onAddEntry` reads its first argument as
              the group to add into, and passing it straight to onClick made that argument the click
              event - a group index no entry sits at, so the add silently did nothing. */}
          <Button
            id="add-field-btn"
            startIcon={<AddRoundedIcon />}
            onClick={() => onAddEntry()}
            sx={{ flex: 'none' }}
          >
            {intl.messages.ADD}
          </Button>
        </ColumnFlexBox>
      )}

      {/* What the region holds, kept for the palette to place again elsewhere. Where it sits on this
          page is not part of it: a preset is arranged wherever it is dropped. */}
      {widgetUsesEntries(widget) && (
        <Button
          id="save-preset-btn"
          startIcon={<BookmarkAddRoundedIcon />}
          onClick={() => onSavePreset(region)}
          sx={{ flex: 'none', justifyContent: 'flex-start' }}
        >
          {intl.messages.SAVE_AS_PRESET}
        </Button>
      )}
    </ColumnFlexBox>
  );
};

export default RegionConfig;
